import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import client from '../services/apollo/apolloClient/index';
import { GET_BLOCKED_USERS } from '../services/apollo/queries/blockQueries';

export const fetchBlockedUsers = createAsyncThunk(
  'blocks/fetchBlockedUsers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_BLOCKED_USERS,
        fetchPolicy: 'network-only',
      });
      return data?.getBlockedUsers || [];
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch blocked users');
    }
  }
);

const initialState = {
  blockedUsers: [],
  blockedUserIds: [],
  loading: false,
  error: null,
  lastFetchedAt: null,
};

const blocksSlice = createSlice({
  name: 'blocks',
  initialState,
  reducers: {
    setBlockedUsers: (state, action) => {
      const list = action.payload || [];
      state.blockedUsers = list;
      state.blockedUserIds = list.map((u) => u._id);
      state.lastFetchedAt = Date.now();
    },
    addBlockedUser: (state, action) => {
      const u = action.payload;
      if (u && !state.blockedUserIds.includes(u._id)) {
        state.blockedUsers.unshift(u);
        state.blockedUserIds.push(u._id);
      }
    },
    removeBlockedUser: (state, action) => {
      const userId = action.payload;
      state.blockedUsers = state.blockedUsers.filter((u) => u._id !== userId);
      state.blockedUserIds = state.blockedUserIds.filter((id) => id !== userId);
    },
    clearBlockedUsers: (state) => {
      state.blockedUsers = [];
      state.blockedUserIds = [];
      state.lastFetchedAt = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlockedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlockedUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.blockedUsers = action.payload;
        state.blockedUserIds = action.payload.map((u) => u._id);
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchBlockedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setBlockedUsers,
  addBlockedUser,
  removeBlockedUser,
  clearBlockedUsers,
} = blocksSlice.actions;

export const selectBlockedUserIds = (state) => state.blocks.blockedUserIds;
export const selectBlockedUsersList = (state) => state.blocks.blockedUsers;
export const selectIsUserBlocked = (userId) => (state) =>
  state.blocks.blockedUserIds.includes(userId);

export default blocksSlice.reducer;
